// TutorialApi.ts
import axios, { AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import Cookies from 'universal-cookie';
import General from '../../../../general';
import config from '../../../../config';
import { PartialBrandTheme, PartialQuestThemeData } from './tutorial.types';
import { ScreenHeadings } from '../modular/types';
import { styleConfig } from './onboarding.types';

export interface UserAuth {
    userId: string;
    token: string;
}

export interface ApiHeaders {
    apiKey: string;
    userId?: string;
    token?: string;
}

export interface OnboardingAction {
    actionId: string;
    actionType: string;
    answers?: string[] | [];
    campaignVariationId?: string;
    isCompleted?: boolean;
    isEnabled?: boolean;
    isRequired?: boolean;
    metadata?: {
        placeholder?: string;
        link?: string;
        manualInput?: string;
        regexKey?: string;
        regexErrorMessage?: string;
        modelType?:string;
    },
    status: boolean;
    options?: string[] | [];
    title: string;
    manualInput?: string;
    // regexKey?: string;
    // regexMessage?: string;
}


interface OnboardingFormData {
    type: string;
    question: string;
    options: Array<string>;
    actionId: string;
    required: boolean;
    placeholder: string;
    linkTitle: string;
    linkUrl: string;
    manualInput: string | boolean;
    regexKey?: string | undefined;
    regexMessage?: string;
    modelType?:string;
    // actionId?: string;
    // actionType?: string;
    // answers?: string[] | [];
    // isRequired?: boolean;
    // metadata?: {
    //     placeholder?: string;
    // };
    // options?: string[] | [];
    // title: string;
    // isCompleted: boolean;
}

export interface UiPropsInterface {
    questThemeData?: PartialQuestThemeData;
    BrandTheme?: {
        properties: PartialBrandTheme;
    };
    controlBtnType?: "Arrow" | "Buttons" | undefined;
    design?: number[][] | undefined
    Headers?: ScreenHeadings[] | [] | undefined,
    loadingTracker?: boolean | undefined;
    multiChoice?: "modal1" | "modal2" | undefined;
    nextBtnText?: string | undefined;
    progress?: string[] | undefined;
    progressBarMultiLine?: boolean | undefined;
    showFooter?: boolean | undefined;
    singleChoose?: "modal1" | "modal2" | "modal3" | undefined;
    template?: "multi-question" | "single-question" | "single-page" | undefined;
    styleConfig?: styleConfig | undefined;
}


export interface QuestResponse {
    success: boolean;
    data: {
        sdkConfig?: {
            uiProps?: UiPropsInterface;
        };
        actions: OnboardingAction[];
        campaignVariationId: string;
    };
}

interface GetUserAuthParams {
    apiKey: string;
    entityId: string;
    uniqueUserId?: string;
    uniqueEmailId?: string;
    userId?: string;
    token?: string;
    entityAuthenticationToken?: string;
}

interface ExternalLoginResponse {

    userId: string;
    token: string;

}

interface VerifyActionResponse {
    success: boolean;
    error?: string;
    data?: any;
}

interface ExternalLoginResponse {
    userId: string;
    token: string;
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
    error?: string;
    userId: string;
    token: string;
}

class TutorialApi {
    private BACKEND_URL: string;
    private GeneralFunctions: General;
    private cookies: Cookies;

    constructor(apiType: string) {
        this.BACKEND_URL = apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
        this.GeneralFunctions = new General("mixpanel", apiType);
        this.cookies = new Cookies();
    }

    private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
        return {
            'Content-Type': 'application/json',
            ...headers
        };
    }

    async getUserAuth({
        apiKey,
        entityId,
        uniqueUserId,
        uniqueEmailId,
        userId,
        token,
        entityAuthenticationToken,
    }: GetUserAuthParams): Promise<UserAuth | null> {
        try {

            const externalUserId = this.cookies.get("externalUserId");
            const questUserId = this.cookies.get("questUserId");
            const questUserToken = this.cookies.get("questUserToken");


            if (
                externalUserId &&
                questUserId &&
                questUserToken &&
                (externalUserId == uniqueUserId || externalUserId == uniqueEmailId)
            ) {
                return {
                    userId: questUserId,
                    token: questUserToken
                };
            }

            if (uniqueUserId) {
                const headers = this.getAxiosHeaders({
                    apiKey,
                    ...(token && { token }),
                    ...(userId && { userId }),
                    ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
                });

                const body = {
                    externalUserId: uniqueUserId,
                    entityId,
                    email: uniqueEmailId || null,
                    ignoreMAU: true,
                };

                const response = await axios.post<ApiResponse<ExternalLoginResponse>>(
                    `${this.BACKEND_URL}api/users/external/login`,
                    body,
                    { headers }
                );


                if (!response.data?.success) {
                    throw new Error('Invalid response from external login');
                }

                const { userId: newUserId, token: newToken } = response.data;
                const expires = new Date();
                expires.setHours(expires.getHours() + 12);

                this.cookies.set("externalUserId", uniqueUserId, { path: "/", expires });
                this.cookies.set("questUserId", newUserId, { path: "/", expires });
                this.cookies.set("questUserToken", newToken, { path: "/", expires });

                return {
                    userId: newUserId,
                    token: newToken
                };
            }

            // If no uniqueUserId but we have userId and token, use those
            if (userId && token) {
                return { userId, token };
            }

            return null;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            return null;
        }
    }

    async fetchCampaignData(
        entityId: string,
        questId: string,
        headers: ApiHeaders,
        variation?: string
    ): Promise<QuestResponse['data']> { // Note the return type change
        try {
            if (!headers.userId || !headers.token) {
                throw new Error('Missing required headers');
            }

            const params = new URLSearchParams();
            params.set("platform", "REACT");
            if (variation) params.set("variation", variation);

            const axiosHeaders = this.getAxiosHeaders(headers);
            const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}?${params.toString()}`;

            const response = await axios.get<QuestResponse>(request, {
                headers: axiosHeaders
            });

            if (!response.data?.data) {
                throw new Error('No data received from API');
            }

            return response.data.data;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }


    async verifyAction(
        entityId: string,
        questId: string,
        headers: ApiHeaders,
        actionId: string,
        campaignVariationId: string
    ): Promise<VerifyActionResponse> {
        try {
            if (!headers.userId || !headers.token) {
                throw new Error('Missing required headers');
            }

            const body = {
                actions: [
                    {
                        answers: [],
                        actionId,
                    },
                ],
                campaignVariationId,
            };

            const axiosHeaders = this.getAxiosHeaders(headers);
            const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}/verify`;

            const response = await axios.post<ApiResponse<VerifyActionResponse>>(
                request,
                body,
                { headers: axiosHeaders }
            );

            if (!response.data?.data) {
                throw new Error('Invalid response from verify action');
            }

            return response.data.data;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }

    processActionsData(actions: OnboardingAction[]): OnboardingFormData[] {

        let data = actions.map((action): OnboardingFormData => ({
            type: action?.actionType,
            question: action?.title,
            options: action?.options || [],
            actionId: action?.actionId,
            required: action?.isRequired,
            placeholder: action?.metadata?.placeholder,
            linkTitle: action?.title || "",
            linkUrl: action?.metadata?.link || "",
            manualInput: Boolean(action?.metadata?.manualInput),
            ...(action?.metadata?.regexKey && { regexKey: action?.metadata?.regexKey }),
            ...(action?.metadata?.regexErrorMessage && { regexMessage: action?.metadata?.regexErrorMessage }),
            ...(action?.metadata?.modelType && { modelType : action?.metadata?.modelType})
        }));

        return data
    }

    clearCookies(): void {
        this.cookies.remove("externalUserId", { path: "/" });
        this.cookies.remove("questUserId", { path: "/" });
        this.cookies.remove("questUserToken", { path: "/" });
    }

    isValidUserAuth(userAuth: unknown): userAuth is UserAuth {
        return (
            !!userAuth &&
            typeof (userAuth as UserAuth).userId === 'string' &&
            typeof (userAuth as UserAuth).token === 'string'
        );
    }



    metricApi = (headers: any, BACKEND_URL: string | undefined, entityId: string, campaignId: string | undefined, campaignVariationId: string, body?: { [key: string]: any },) => {
        const api = async (page: string) => {
            try {
                const url = `${BACKEND_URL}api/entities/${entityId}/metrics/${page}/campaign`;
                const response = await axios.post(url, { campaignId, campaignVariationId, ...body }, { headers });

                if (response.status === 200 && response.data.success) {
                    return response;
                }
                // else {
                //   throw new Error(response.statusText);
                // }
            } catch (error: any) {
                if (error.response && error.response.data && error.response.data.error) {
                    console.error('API request failed:', error.response.data.error);
                } else {
                    console.error('API request failed:', error.message);
                }
                // throw error;
            }
        };

        return {
            async view() {
                return api('onboarding-view');
            },
            async page(pageNumber: number) {
                return api(`onboarding-complete-page-${pageNumber}`);
            },
            async complete() {
                return api('onboarding-complete');
            }
        };
    };

    // verifyCampaigns = (BACKEND_URL: string, entityId: string, campaignId: string, campaignVariationId: string, headers: any, body?: { [key: string]: any },) => {

    //     try {
    //         const url = `${BACKEND_URL}api/entities/${entityId}/metrics/${page}/campaign`;
    //         const response = await axios.post(url, { campaignId, campaignVariationId, ...body }, { headers });

    //         if (response.status === 200 && response.data.success) {
    //             return response;
    //         }
    //         // else {
    //         //   throw new Error(response.statusText);
    //         // }
    //     } catch (error: any) {
    //         if (error.response && error.response.data && error.response.data.error) {
    //             console.error('API request failed:', error.response.data.error);
    //         } else {
    //             console.error('API request failed:', error.message);
    //         }
    //         // throw error;
    //     }

    // };
}

export default TutorialApi;